type ApiCall = {
  endpoint: string;
  timestamp: number;
  sessionMs: number;
};

type Report = {
  total: number;
  byEndpoint: Record<string, number>;
  calls: ApiCall[];
  durationMs: number;
};

const STORAGE_KEY = "__intiza_api_tracking";

class ApiCallTracker {
  private static calls: ApiCall[] = [];
  private static sessionStart: number = Date.now();

  static get enabled(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  }

  /** Activar desde consola del browser: __apiTracker.enable() */
  static enable() {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* noop */
    }
    this.calls = [];
    this.sessionStart = Date.now();
    console.log(
      "[ApiTracker] ✅ Tracking activado — navega a la pantalla a medir"
    );
  }

  /** Detener y limpiar: __apiTracker.disable() */
  static disable() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
    console.log("[ApiTracker] ⛔ Tracking desactivado");
  }

  /** Resetear contador sin desactivar: __apiTracker.reset() */
  static reset() {
    this.calls = [];
    this.sessionStart = Date.now();
    console.log("[ApiTracker] 🔄 Contador reseteado");
  }

  /** Llamado internamente por RequestService */
  static track(endpoint: string) {
    if (!this.enabled) return;
    const now = Date.now();
    this.calls.push({
      endpoint,
      timestamp: now,
      sessionMs: now - this.sessionStart
    });
  }

  /** Reporte agrupado: __apiTracker.report() */
  static report(): Report {
    const durationMs = Date.now() - this.sessionStart;
    const byEndpoint: Record<string, number> = {};
    this.calls.forEach((c) => {
      byEndpoint[c.endpoint] = (byEndpoint[c.endpoint] ?? 0) + 1;
    });

    console.group(
      `[ApiTracker] 📊 Reporte — ${this.calls.length} llamadas en ${durationMs}ms`
    );
    console.table(
      Object.entries(byEndpoint)
        .sort((a, b) => b[1] - a[1])
        .map(([endpoint, count]) => ({ endpoint, count }))
    );
    console.groupEnd();

    return {
      total: this.calls.length,
      byEndpoint,
      calls: this.calls,
      durationMs
    };
  }

  /** Timeline cronológico: __apiTracker.timeline() */
  static timeline() {
    console.group("[ApiTracker] ⏱ Timeline");
    this.calls.forEach((c) => {
      console.log(
        `  +${String(c.sessionMs).padStart(5, " ")}ms  ${c.endpoint}`
      );
    });
    console.groupEnd();
  }

  /** Copiar reporte como JSON al portapapeles: __apiTracker.copy() */
  static copy() {
    const data = this.report();
    try {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      console.log("[ApiTracker] 📋 Reporte copiado al portapapeles");
    } catch {
      console.log("[ApiTracker] No se pudo copiar. Datos:", data);
    }
    return data;
  }
}

// Exponer en window para uso desde la consola del browser
(window as any).__apiTracker = ApiCallTracker;

// Si ya estaba activo antes de un reload, avisarlo
if (ApiCallTracker.enabled) {
  console.log(
    "[ApiTracker] 🟡 Tracking activo (persistido del reload anterior) — usa __apiTracker.reset() para iniciar medición limpia"
  );
}

export default ApiCallTracker;
